/**
 * AvatarEditor — LINEスタイルのアバター編集コンポーネント
 * - 画像あり: 画像表示 + ホバーでカメラアイコン
 * - 画像なし: イニシャル入りカラーサークル + ホバーでカメラアイコン
 * - クリック: ファイルピッカー → アップロード
 * - 長押し or 右クリック: 削除メニュー
 */
import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Trash2, Loader, RotateCw, X, Save } from 'lucide-react';
import { getToken } from '../services/api';

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? 'http://localhost:3001/api';

// 取得済み画像の Object URL をパス単位でキャッシュする（モジュール全体で共有）。
// これにより、リストの再描画・再マウントのたびに画像を取り直して「出たり消えたり」
// する現象を防ぐ（初期表示からキャッシュ済みURLを使うのでちらつかない）。
const avatarUrlCache = new Map<string, string>();

/** イニシャルから背景色を決定する（一貫したハッシュベース） */
function nameToColor(name: string): string {
  const colors = [
    '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
    '#10B981', '#06B6D4', '#EF4444', '#6366F1',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length] ?? colors[0]!;
}

function initials(name: string): string {
  return name ? name.slice(0, 1) : '?';
}

interface AvatarEditorProps {
  /** ユーザーまたは船舶のID */
  entityId: string;
  /** アップロード先: 'user' → /api/users/:id/avatar, 'vessel' → /api/vessels/:id/image */
  type: 'user' | 'vessel';
  /** 現在の画像パス（DBから取得済みのファイル名） */
  imagePath?: string | null;
  /** 表示名（イニシャル生成用） */
  displayName: string;
  /** アップロード/削除完了後のコールバック */
  onUpdated: (newPath: string | null) => void;
  /** アバターサイズ (px), default: 40 */
  size?: number;
  /** 編集不可モード（閲覧専用） */
  readOnly?: boolean;
}

const AvatarEditor: React.FC<AvatarEditorProps> = ({
  entityId,
  type,
  imagePath,
  displayName,
  onUpdated,
  size = 40,
  readOnly = false,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);   // 中央プレビューモーダル
  const [rotation, setRotation] = useState(0);             // プレビュー中の回転角（90刻み）
  const [hover, setHover] = useState(false);
  const cacheKey = imagePath ? `${type}:${imagePath}` : null;
  const [imageUrl, setImageUrl] = useState<string | null>(cacheKey ? (avatarUrlCache.get(cacheKey) ?? null) : null);

  // プレビュー表示中は ESC で閉じる
  useEffect(() => {
    if (!previewOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { setPreviewOpen(false); setRotation(0); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [previewOpen]);

  // 認証付きで画像を取得し blob URL を生成する。
  // 以前は <img src="...?token=JWT"> だったが、JWT を URL に載せない／CSP の
  // img-src を狭められるよう fetch + Authorization ヘッダー + blob 方式に変更。
  useEffect(() => {
    if (!imagePath || !cacheKey) { setImageUrl(null); return; }
    const cached = avatarUrlCache.get(cacheKey);
    if (cached) { setImageUrl(cached); return; }   // キャッシュヒット → 取り直さない
    const endpoint = `${API_BASE}/${type === 'user' ? 'avatars' : 'vessel-images'}/${imagePath}`;
    const tok = getToken();
    let cancelled = false;
    fetch(endpoint, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
      .then(r => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(b => {
        const objectUrl = URL.createObjectURL(b);
        avatarUrlCache.set(cacheKey, objectUrl);   // 共有キャッシュへ保存（revokeしない）
        if (!cancelled) setImageUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setImageUrl(null); });
    return () => { cancelled = true; };  // キャッシュ共有のため Object URL は revoke しない
  }, [imagePath, type, cacheKey]);

  const uploadEndpoint = type === 'user'
    ? `${API_BASE}/users/${entityId}/avatar`
    : `${API_BASE}/vessels/${entityId}/image`;

  const deleteEndpoint = uploadEndpoint;
  const fieldName = type === 'user' ? 'avatar' : 'image';
  const fontSize = Math.max(12, Math.round(size * 0.38));

  function authHeaders(): Record<string, string> {
    const tok = getToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append(fieldName, file);
      const res = await fetch(uploadEndpoint, { method: 'POST', body: fd, headers: authHeaders() });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      const d = json.data ?? json;
      setPreviewOpen(false);
      setRotation(0);
      onUpdated(d.avatar_path ?? d.image_path ?? d.path ?? null);
    } catch {
      alert('アップロードに失敗しました');
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('この画像を削除しますか？')) return;
    setUploading(true);
    try {
      const res = await fetch(deleteEndpoint, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Delete failed');
      setPreviewOpen(false);
      setRotation(0);
      onUpdated(null);
    } catch {
      alert('削除に失敗しました');
    }
    setUploading(false);
  };

  // プレビューで回転した状態を保存する（canvasで回転→再アップロード）
  const handleRotateSave = async () => {
    if (!imageUrl || rotation % 360 === 0) return;
    setUploading(true);
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('load failed'));
        img.src = imageUrl;
      });
      const rot = ((rotation % 360) + 360) % 360;
      const canvas = document.createElement('canvas');
      const swap = rot === 90 || rot === 270;
      canvas.width = swap ? img.naturalHeight : img.naturalWidth;
      canvas.height = swap ? img.naturalWidth : img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas unavailable');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rot * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9));
      const fd = new FormData();
      fd.append(fieldName, blob, 'rotated.jpg');
      const res = await fetch(uploadEndpoint, { method: 'POST', body: fd, headers: authHeaders() });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      const d = json.data ?? json;
      setRotation(0);
      setPreviewOpen(false);
      onUpdated(d.avatar_path ?? d.image_path ?? d.path ?? null);
    } catch {
      alert('回転の保存に失敗しました');
    }
    setUploading(false);
  };

  const bgColor = nameToColor(displayName);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}
    >
      {/* メインアバター */}
      <div
        onClick={() => {
          if (readOnly || uploading) return;
          if (imagePath) { setRotation(0); setPreviewOpen(true); }
          else { fileRef.current?.click(); }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          background: imageUrl ? '#e5e7eb' : bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: readOnly || uploading ? 'default' : 'pointer',
          position: 'relative',
          flexShrink: 0,
          border: '2px solid rgba(255,255,255,0.8)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          transition: 'box-shadow 0.15s',
          ...(hover && !readOnly ? { boxShadow: '0 2px 8px rgba(0,0,0,0.2)' } : {}),
        }}
      >
        {/* 画像 or イニシャル */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={displayName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ color: '#fff', fontWeight: 700, fontSize, userSelect: 'none' }}>
            {initials(displayName)}
          </span>
        )}

        {/* ローディングオーバーレイ */}
        {uploading && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
          }}>
            <Loader size={size * 0.45} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* ホバーオーバーレイ（編集可能時） */}
        {!uploading && !readOnly && hover && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
          }}>
            <Camera size={size * 0.38} color="#fff" />
          </div>
        )}
      </div>

      {/* 画像プレビューモーダル（画面中央・portal描画のためテーブル等にクリップされない） */}
      {previewOpen && !readOnly && createPortal(
        <div
          onClick={() => { if (!uploading) { setPreviewOpen(false); setRotation(0); } }}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(15,23,42,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: 20,
              width: 'min(560px, 92vw)', maxHeight: '90vh',
              display: 'flex', flexDirection: 'column', gap: 14,
              boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
            }}
          >
            {/* ヘッダー */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                {displayName} の{type === 'user' ? 'アイコン画像' : '船舶画像'}
              </span>
              <button
                onClick={() => { setPreviewOpen(false); setRotation(0); }}
                disabled={uploading}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* プレビュー */}
            <div style={{
              background: '#0f172a', borderRadius: 12, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 240, maxHeight: '58vh', padding: 12,
            }}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={displayName}
                  style={{
                    maxWidth: '100%', maxHeight: '54vh', objectFit: 'contain',
                    transform: `rotate(${rotation}deg)`, transition: 'transform .2s',
                    // 90/270度回転時にはみ出さないよう縮小
                    ...(rotation % 180 !== 0 ? { maxHeight: 'min(54vh, 480px)', maxWidth: 'min(54vh, 480px)' } : {}),
                  }}
                />
              ) : (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>画像を読み込み中…</span>
              )}
            </div>

            {/* 操作ボタン */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer' }}
              >
                <Camera size={14} /> 画像を変更
              </button>
              <button
                onClick={() => setRotation(r => (r + 90) % 360)}
                disabled={uploading || !imageUrl}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <RotateCw size={14} /> 回転
              </button>
              {rotation % 360 !== 0 && (
                <button
                  onClick={handleRotateSave}
                  disabled={uploading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: 'none', background: '#0369a1', color: '#fff', fontSize: 13, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer' }}
                >
                  <Save size={14} /> {uploading ? '保存中…' : 'この向きで保存'}
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={uploading}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1.5px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                <Trash2 size={14} /> 削除
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              回転した場合は「この向きで保存」を押すと反映されます（ESCまたは背景クリックで閉じる）
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 隠しファイルインプット */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default AvatarEditor;
